import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { Divider, InfoTooltip } from "@impero-com/ui";
import i18next from "i18next";
import memoizeOne from "memoize-one";

import {
    SubsequentStepIndex,
    DayOfWeek,
    WeeklyRecurrenceRule,
    UpdatableWeeklyRecurrenceRule,
} from "api/generated/model";
import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { MultiSelectInput } from "components/form/legacyFields/multiSelectInput";
import { isNotUndefined } from "utils/typePredicates";

import { ErrorMap, ErrorValue, getErrorMessage } from "../common/errors";
import { WithArrays } from "../common/utils";

import { MultiWeekdayCheckboxes } from "./multiWeekdayCheckboxes";

const FixedWidthWrapper = ({ children }: { children: React.ReactNode }) => {
    return <div style={{ maxWidth: 150 }}>{children}</div>;
};

const periodicityLabels = memoizeOne((_language: string) => {
    return {
        options: [
            {
                value: "1",
                label: t("CreateControl_Reccurence_Weekly_Periodicity_1"),
            },
            {
                value: "2",
                label: t("CreateControl_Reccurence_Weekly_Periodicity_2"),
            },
            {
                value: "3",
                label: t("CreateControl_Reccurence_Weekly_Periodicity_3"),
            },
            {
                value: "4",
                label: t("CreateControl_Reccurence_Weekly_Periodicity_4"),
            },
            {
                value: "5",
                label: t("CreateControl_Reccurence_Weekly_Periodicity_5"),
            },
            {
                value: "6",
                label: t("CreateControl_Reccurence_Weekly_Periodicity_6"),
            },
        ],
        label: t("CreateControl_Reccurence_Weekly_Every"),
    };
});

abstract class BaseWeeklyRecurrenceRuleUpdaterForm<
    P,
> extends React.PureComponent<P> {
    render = () => {
        return (
            <>
                <Divider />
                {this.buildWeekdays()}
                {this.buildPeriodicity()}
                <Divider />
                <h1>
                    {t("CreateControl_Planning_Title")}&nbsp;
                    <InfoTooltip
                        text={t("Control_ModifySchedule_PlanningInfo")}
                    />
                </h1>
                {this.buildResponsibleAllocatedTimeInputs()}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep0")}
                {this.buildReviewerAllocatedTimeInputs("SubsequentStep1")}
                <Divider />
                {this.buildRemindersWidget()}
            </>
        );
    };

    abstract buildWeekdays(): void;

    abstract buildPeriodicity(): void;

    abstract buildResponsibleAllocatedTimeInputs(): React.ReactNode;

    abstract buildReviewerAllocatedTimeInputs(
        subsequentStepIndex: SubsequentStepIndex
    ): React.ReactNode;

    abstract buildRemindersWidget(): React.ReactNode;
}

const toWeekDayArraysRecord = memoizeOne(
    (days: DayOfWeek[][]): Record<DayOfWeek, boolean[]> => {
        return {
            Monday: days.map((ds) => ds.includes("Monday")),
            Tuesday: days.map((ds) => ds.includes("Tuesday")),
            Wednesday: days.map((ds) => ds.includes("Wednesday")),
            Thursday: days.map((ds) => ds.includes("Thursday")),
            Friday: days.map((ds) => ds.includes("Friday")),
            Saturday: days.map((ds) => ds.includes("Saturday")),
            Sunday: days.map((ds) => ds.includes("Sunday")),
        };
    }
);

class PartialWeeklyRecurrenceRuleUpdater extends BaseWeeklyRecurrenceRuleUpdaterForm<PartialWeeklyRecurrenceRuleUpdaterProps> {
    buildWeekdays = () => {
        const {
            currentRule: { days },
            getFieldError,
            errors,
        } = this.props;
        const values = toWeekDayArraysRecord(days);
        return (
            <BaseFormDisplay
                label={t("Control_Scheduling_Weekdays")}
                error={getErrorMessage(getFieldError(errors, "days"))}
            >
                <MultiWeekdayCheckboxes
                    type="EDIT"
                    days={values}
                    onChange={this.handleDaysChange}
                />
            </BaseFormDisplay>
        );
    };

    private handleDaysChange = (day: DayOfWeek, value: boolean) => {
        this.props.onChangePartialWeekdays(day, value);
    };

    buildPeriodicity = () => {
        const { currentRule } = this.props;

        const values = currentRule.periodicity.map((val) => val.toString());
        return (
            <MultiSelectInput
                type="EDIT"
                label={periodicityLabels(i18next.language).label}
                name={periodicityLabels(i18next.language).label}
                values={values}
                options={periodicityLabels(i18next.language).options}
                onChange={this.handlePeriodicitysChange}
            />
        );
    };

    private handlePeriodicitysChange = (v: string | undefined) => {
        const { onChangePeriodicity } = this.props;
        const periodicity = Number(v);
        if (!isNaN(periodicity)) {
            onChangePeriodicity(periodicity);
        }
    };

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface BasePartialWeeklyRecurrenceRuleProps {
    currentRule: WithArrays<WeeklyRecurrenceRule>;
    buildResponsibleAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY"
    ) => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT" | "READ_ONLY",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildRemindersWidget: (readOnly: "EDIT" | "READ_ONLY") => React.ReactNode;
}

type PartialWeeklyRecurrenceRuleUpdaterProps =
    BasePartialWeeklyRecurrenceRuleProps &
        UpdatableWeeklyRecurrenceRule & {
            onChangePeriodicity: (periodicity: number) => void;
            onChangePartialWeekdays: (day: DayOfWeek, value: boolean) => void;
            getFieldError: (
                errors: Partial<ErrorMap<keyof WeeklyRecurrenceRule>>,
                key: keyof WeeklyRecurrenceRule
            ) => ErrorValue;
            errors: Partial<ErrorMap<keyof WeeklyRecurrenceRule>>;
        };

type PartialWeeklyRecurrenceRuleViewerProps =
    BasePartialWeeklyRecurrenceRuleProps;

class PartialWeeklyRecurrenceRuleViewer extends BaseWeeklyRecurrenceRuleUpdaterForm<PartialWeeklyRecurrenceRuleViewerProps> {
    buildWeekdays = () => {
        const {
            currentRule: { days },
        } = this.props;
        const values = toWeekDayArraysRecord(days);
        return (
            <BaseFormDisplay label={t("Control_Scheduling_Weekdays")}>
                <MultiWeekdayCheckboxes type="READ_ONLY" days={values} />
            </BaseFormDisplay>
        );
    };

    buildPeriodicity = () => {
        const { currentRule } = this.props;
        const values = currentRule.periodicity
            ?.map((val) => val?.toString())
            .filter(isNotUndefined);
        return (
            <FixedWidthWrapper>
                <MultiSelectInput
                    type="READ_ONLY"
                    label={periodicityLabels(i18next.language).label}
                    name={periodicityLabels(i18next.language).label}
                    values={values}
                    options={periodicityLabels(i18next.language).options}
                    display={"Input"}
                />
            </FixedWidthWrapper>
        );
    };

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("READ_ONLY");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "READ_ONLY",
            subsequentStepIndex
        );

    buildRemindersWidget = () => this.props.buildRemindersWidget("READ_ONLY");
}

class FullWeeklyRecurrenceRuleUpdater extends BaseWeeklyRecurrenceRuleUpdaterForm<FullWeeklyRecurrenceRuleUpdaterProps> {
    buildWeekdays = () => {
        const { currentRule, getFieldError, errors } = this.props;
        return (
            <BaseFormDisplay
                label={t("Control_Scheduling_Weekdays")}
                error={getErrorMessage(getFieldError(errors, "days"))}
            >
                <MultiWeekdayCheckboxes
                    type="EDIT"
                    days={toWeekDayArraysRecord(currentRule.days)}
                    onChange={this.handleDaysChange}
                />
            </BaseFormDisplay>
        );
    };

    private handleDaysChange = (day: DayOfWeek, value: boolean) => {
        this.props.onChangeFullWeekdays(day, value);
    };

    buildPeriodicity = () => {
        const { currentRule } = this.props;
        return (
            <MultiSelectInput
                type="EDIT"
                label={periodicityLabels(i18next.language).label}
                name={periodicityLabels(i18next.language).label}
                values={currentRule.periodicity.map((val) => val.toString())}
                options={periodicityLabels(i18next.language).options}
                onChange={this.handlePeriodicitysChange}
            />
        );
    };

    private handlePeriodicitysChange = (v: string | undefined) => {
        const { onChangePeriodicity } = this.props;
        const periodicity = Number(v);
        if (!isNaN(periodicity)) {
            onChangePeriodicity(periodicity);
        }
    };

    buildResponsibleAllocatedTimeInputs = () =>
        this.props.buildResponsibleAllocatedTimeInputs("EDIT");

    buildReviewerAllocatedTimeInputs = (
        subsequentStepIndex: SubsequentStepIndex
    ) =>
        this.props.buildReviewerAllocatedTimeInputs(
            "EDIT",
            subsequentStepIndex
        );

    buildRemindersWidget = () => this.props.buildRemindersWidget("EDIT");
}

interface FullWeeklyRecurrenceRuleUpdaterProps {
    currentRule: WithArrays<WeeklyRecurrenceRule>;
    buildRemindersWidget: (readOnly: "EDIT") => React.ReactNode;
    buildReviewerAllocatedTimeInputs: (
        readOnly: "EDIT",
        subsequentStepIndex: SubsequentStepIndex
    ) => React.ReactNode;
    buildResponsibleAllocatedTimeInputs: (readOnly: "EDIT") => React.ReactNode;
    onChangeFullWeekdays: (day: DayOfWeek, value: boolean) => void;
    onChangePeriodicity: (periodicity: number) => void;
    getFieldError: (
        errors: Partial<ErrorMap<keyof WeeklyRecurrenceRule>>,
        key: keyof WeeklyRecurrenceRule
    ) => ErrorValue;
    errors: Partial<ErrorMap<keyof WeeklyRecurrenceRule>>;
}
export type {
    BasePartialWeeklyRecurrenceRuleProps,
    PartialWeeklyRecurrenceRuleViewerProps,
    FullWeeklyRecurrenceRuleUpdaterProps,
};
export {
    PartialWeeklyRecurrenceRuleUpdater,
    PartialWeeklyRecurrenceRuleViewer,
    FullWeeklyRecurrenceRuleUpdater,
};
